import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, ChevronRight, Loader2, MessageSquare, Send, Calendar,
  LogOut, ArrowLeft, Check, TrendingUp, AlertCircle, Target, Sparkles,
  BookOpen, Users, Mail,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { DarkModeToggle } from "../components/DarkModeToggle";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SupervisorPortal — minimal oversight surface for supervising ADIs.
 * Does NOT require the supervisor to migrate their business to this platform.
 * Shows assigned trainees, readiness overview, coaching notes, optional calendar glance.
 */
export default function SupervisorPortal() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [trainees, setTrainees] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const [traineeResponse, inviteResponse] = await Promise.all([
        axios.get(`${API}/api/supervisor/trainees`, { withCredentials: true }),
        axios.get(`${API}/api/organisation/invites/mine`, { withCredentials: true }),
      ]);
      setTrainees(traineeResponse.data.items || []);
      setPendingInvites((inviteResponse.data.items || []).filter((item) => item.status === "pending"));
    } catch {
      setTrainees([]);
      setPendingInvites([]);
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <LoadingScreen />;

  if (selected) return <TraineeDetail rel={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <Shield className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-extrabold text-slate-900 dark:text-white">Supervisor Portal</p>
            <p className="text-[10px] text-slate-400">Light oversight · {user?.email}</p>
          </div>
          <DarkModeToggle />
          <button onClick={logout} data-testid="btn-logout" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <LogOut className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 flex items-start gap-3">
          <Users className="w-5 h-5 text-indigo-500 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white">Your Trainees</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Lightweight view — you don't need to run your own business on this platform.
            </p>
          </div>
        </div>

        {trainees.length === 0 && pendingInvites.length === 0 && (
          <div data-testid="no-trainees" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-8 text-center">
            <p className="text-sm font-bold text-slate-900 dark:text-white">No trainees yet</p>
            <p className="text-xs text-slate-500 mt-1">Invite a PDI from TrainerPath. Pending invitations stay visible until the PDI accepts.</p>
          </div>
        )}

        {pendingInvites.length > 0 && (
          <section data-testid="pending-pdi-invites" className="space-y-2">
            <p className="text-xs font-extrabold uppercase tracking-wide text-amber-700">Pending acceptance</p>
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/20">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-extrabold text-slate-900 dark:text-white">{invite.intended_name || invite.intended_email}</p>
                    <p className="truncate text-[11px] text-slate-500">{invite.intended_email}</p>
                    <p className="mt-1 font-mono text-[11px] text-slate-500">{invite.code}</p>
                  </div>
                  <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] font-bold uppercase text-amber-800">Pending</span>
                </div>
                <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">This PDI will move into Active Trainees as soon as they accept the activation.</p>
              </div>
            ))}
          </section>
        )}

        {/* Weekly Digest panel */}
        {trainees.length > 0 && <WeeklyDigestPanel onOpen={(pdiId) => {
          const rel = trainees.find(t => t.pdi_user_id === pdiId);
          if (rel) setSelected(rel);
        }} />}

        {trainees.map(t => (
          <button key={t.id} data-testid={`trainee-${t.id}`} onClick={() => setSelected(t)}
            className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 flex items-center gap-3 hover:border-indigo-300 active:scale-[0.98] transition-all text-left">
            <div className="w-11 h-11 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <span className="text-sm font-extrabold text-indigo-700 dark:text-indigo-400">
                {(t.pdi_name || t.pdi_email || "?").substring(0,1).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">{t.pdi_name || t.pdi_email}</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {t.lessons_debriefed} lesson{t.lessons_debriefed !== 1 ? "s" : ""} · ✓ {t.demonstrated_count} ✗ {t.missed_count}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Individual trainee detail ──
function TraineeDetail({ rel, onBack }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [category, setCategory] = useState("general");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/supervisor/trainee/${rel.pdi_user_id}/overview`, { withCredentials: true });
      setOverview(data);
    } catch {}
    setLoading(false);
  }, [rel.pdi_user_id]);
  useEffect(() => { load(); }, [load]);

  const submitNote = async () => {
    if (!note.trim()) { toast.error("Write a note"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/supervisor/trainee/${rel.pdi_user_id}/notes`,
        { text: note, category }, { withCredentials: true });
      toast.success("Note sent to trainee");
      setNote("");
      await load();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  if (loading) return <LoadingScreen />;
  if (!overview) return null;

  const perms = overview.permissions || {};

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="back-to-trainees" onClick={onBack} className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-slate-900 dark:text-white truncate">{overview.pdi_name}</p>
            <p className="text-[10px] text-slate-400 truncate">{overview.pdi_email}</p>
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 flex gap-1 border-t border-slate-100 dark:border-slate-700">
          {["overview", "notes"].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`flex-1 h-10 text-xs font-bold border-b-2 transition-colors ${
                tab === t ? "border-indigo-500 text-indigo-600" : "border-transparent text-slate-400"
              }`}>
              {t === "overview" ? "Overview" : `Coaching Notes (${overview.recent_notes?.length || 0})`}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {tab === "overview" && (
          <>
            {/* Readiness */}
            <div data-testid="sup-readiness" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-5">
              <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wider mb-1">Part 3 Readiness</p>
              <div className="flex items-end gap-3">
                <p className="text-5xl font-extrabold text-slate-900 dark:text-white tabular-nums">
                  {overview.readiness_score}<span className="text-xl text-slate-400">/100</span>
                </p>
                <p className="text-xs text-slate-500 mb-2">
                  {overview.competencies_tested}/{overview.competencies_total} scored · {overview.lessons_debriefed} debriefed
                </p>
              </div>
            </div>

            {/* Competency breakdown */}
            {perms.competencies && overview.per_competency && (
              <CompetencyList items={overview.per_competency} />
            )}

            {/* Recent debriefs */}
            {perms.competencies && overview.latest_events?.length > 0 && (
              <div data-testid="sup-recent-lessons" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-2">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Recent Debriefs</p>
                {overview.latest_events.slice(0,3).map((ev, i) => (
                  <div key={i} className="border-l-2 border-indigo-200 dark:border-indigo-800 pl-3 py-1">
                    <p className="text-[10px] text-slate-400">{new Date(ev.created_at).toLocaleDateString()}</p>
                    <div className="text-xs text-slate-700 dark:text-slate-300">
                      {ev.demonstrated?.length > 0 && <p><span className="font-bold text-emerald-600">✓ Demonstrated:</span> {ev.demonstrated.length}</p>}
                      {ev.missed?.length > 0 && <p><span className="font-bold text-red-500">✗ Missed:</span> {ev.missed.length}</p>}
                    </div>
                    {ev.improvement_tip && <p className="text-[11px] italic text-slate-500 mt-1">"{ev.improvement_tip}"</p>}
                  </div>
                ))}
              </div>
            )}

            {/* End-of-day reflections */}
            {perms.lesson_reviews && overview.recent_eod?.length > 0 && (
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-2">
                <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wider">Recent Daily Reflections</p>
                {overview.recent_eod.slice(0,3).map((e, i) => (
                  <div key={i} className="text-xs">
                    <p className="text-[10px] text-slate-400">{e.date} · {e.lessons_today} lesson{e.lessons_today !== 1 ? "s" : ""}</p>
                    {e.ai_summary && <p className="text-slate-700 dark:text-slate-300 italic leading-relaxed">"{e.ai_summary}"</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Upcoming lessons if permitted */}
            {perms.calendar && overview.upcoming_lessons && (
              <div data-testid="sup-upcoming" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Upcoming Lessons</p>
                </div>
                <div className="space-y-1.5">
                  {overview.upcoming_lessons.slice(0,5).map(l => (
                    <div key={l.id} className="text-xs flex gap-2">
                      <span className="text-slate-400 tabular-nums">{l.date}</span>
                      <span className="text-slate-400">{l.start_time}</span>
                      <span className="text-slate-700 dark:text-slate-300 truncate">{l.title || "Lesson"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {tab === "notes" && (
          <>
            {perms.notes && (
              <div data-testid="sup-add-note" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 space-y-3">
                <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">Add Coaching Note</p>
                <select data-testid="note-category" value={category} onChange={e => setCategory(e.target.value)}
                  className="w-full h-9 border border-slate-200 dark:border-slate-600 rounded-xl px-3 text-xs bg-white dark:bg-slate-700">
                  <option value="general">General</option>
                  <option value="competency">Competency Coaching</option>
                  <option value="lesson_review">Lesson Review</option>
                </select>
                <textarea data-testid="note-text" value={note} onChange={e => setNote(e.target.value)}
                  placeholder="e.g. Try more Socratic questioning at roundabouts — leave 2-3 seconds after the question..."
                  className="w-full h-24 border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2 text-xs bg-white dark:bg-slate-700 resize-none" />
                <Button data-testid="submit-note" onClick={submitNote} disabled={saving}
                  className="w-full h-10 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Send to trainee</>}
                </Button>
              </div>
            )}

            {overview.recent_notes?.length > 0 ? (
              <div className="space-y-2">
                {overview.recent_notes.map(n => (
                  <div key={n.id} data-testid={`note-${n.id}`} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 uppercase">{n.category.replace("_", " ")}</span>
                      <span className="text-[10px] text-slate-400 ml-auto">{new Date(n.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{n.text}</p>
                    <p className="text-[10px] text-slate-400 mt-1">— {n.supervisor_name}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-4">No notes yet</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CompetencyList({ items }) {
  const byCat = {};
  items.forEach(c => { (byCat[c.category] ??= []).push(c); });
  const dotColor = s => ({ strong: "bg-emerald-500", developing: "bg-amber-400", weak: "bg-red-400", untested: "bg-slate-200" }[s]);
  return (
    <div data-testid="sup-competencies" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Target className="w-4 h-4 text-indigo-500" />
        <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">17 Competencies</p>
      </div>
      {Object.entries(byCat).map(([cat, list]) => (
        <div key={cat} className="mt-2.5">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{cat}</p>
          <div className="space-y-1">
            {list.map(c => (
              <div key={c.id} className="flex items-start gap-2 text-xs">
                <div className={`w-2 h-2 rounded-full mt-1.5 ${dotColor(c.status)}`} />
                <p className={`flex-1 leading-tight ${c.status === "weak" ? "text-red-600" : c.status === "strong" ? "text-emerald-600" : c.status === "developing" ? "text-amber-700" : "text-slate-400"}`}>
                  {c.label}
                  {c.score != null && <span className="ml-1 text-[10px] text-slate-400 font-bold">· {c.score}</span>}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120]">
      <Loader2 className="w-10 h-10 animate-spin text-indigo-500" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   WeeklyDigestPanel — surfaces this week's trainee summary with
   one-click actions (send coaching note, open trainee overview).
   ═══════════════════════════════════════════════════════════════ */
function WeeklyDigestPanel({ onOpen }) {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickNoteFor, setQuickNoteFor] = useState(null);
  const [noteBody, setNoteBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/supervisor/digest/weekly`, { withCredentials: true });
        setDigest(data);
      } catch { /* noop */ }
      setLoading(false);
    })();
  }, []);

  const sendNote = async (pdiId) => {
    if (!noteBody.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/supervisor/trainee/${pdiId}/notes`,
        { text: noteBody }, { withCredentials: true });
      toast.success("Coaching note sent");
      setQuickNoteFor(null);
      setNoteBody("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send note");
    }
    setSending(false);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
        <span className="text-xs text-slate-500">Building your weekly digest…</span>
      </div>
    );
  }
  if (!digest || !digest.trainees?.length) return null;

  return (
    <div data-testid="weekly-digest" className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border border-indigo-200 dark:border-indigo-800 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
          <Mail className="w-4 h-4 text-indigo-600" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-extrabold tracking-wider text-indigo-600 uppercase">Weekly Digest</p>
          <p className="text-[10px] text-slate-500 dark:text-slate-400">
            {digest.total_trainees} trainee{digest.total_trainees === 1 ? "" : "s"} · {digest.total_lessons_this_week} lesson{digest.total_lessons_this_week === 1 ? "" : "s"} this week · {digest.total_repeat_weaknesses} repeat weak area{digest.total_repeat_weaknesses === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <div className="space-y-2.5">
        {digest.trainees.map(t => (
          <div key={t.pdi_user_id} data-testid={`digest-trainee-${t.pdi_user_id}`}
            className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-slate-100 dark:border-slate-700 space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-extrabold text-indigo-700 dark:text-indigo-400">
                  {(t.pdi_name || t.pdi_email || "?").substring(0, 1).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{t.pdi_name || t.pdi_email}</p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  {t.lessons_this_week} lesson{t.lessons_this_week === 1 ? "" : "s"} · readiness {t.readiness_now}
                  {t.readiness_delta !== 0 && (
                    <span className={`ml-1 font-bold ${t.readiness_delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                      ({t.readiness_delta > 0 ? "+" : ""}{t.readiness_delta})
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Wins */}
            {t.wins.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {t.wins.slice(0, 3).map(w => (
                  <span key={w.id} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                    ✓ {w.title} ×{w.count}
                  </span>
                ))}
              </div>
            )}

            {/* Repeat weaknesses */}
            {t.repeat_weaknesses.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {t.repeat_weaknesses.slice(0, 3).map(w => (
                  <span key={w.id} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                    ✗ {w.title} ×{w.count}
                  </span>
                ))}
              </div>
            )}

            {/* Suggested focus */}
            {t.suggested_focus && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-2 py-1.5 flex items-start gap-1.5">
                <Target className="w-3 h-3 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-[10px] text-amber-800 dark:text-amber-200">
                  <span className="font-bold">Focus next:</span> {t.suggested_focus.title} <span className="text-amber-600">· {t.suggested_focus.reason}</span>
                </p>
              </div>
            )}

            {/* Quick actions */}
            {quickNoteFor === t.pdi_user_id ? (
              <div className="space-y-1.5">
                <textarea
                  data-testid={`digest-note-input-${t.pdi_user_id}`}
                  value={noteBody} onChange={e => setNoteBody(e.target.value)}
                  placeholder={`Quick coaching note for ${t.pdi_name || "your trainee"}…`}
                  className="w-full text-xs p-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700"
                  rows={2}
                />
                <div className="flex gap-1.5">
                  <button data-testid={`digest-send-${t.pdi_user_id}`} onClick={() => sendNote(t.pdi_user_id)}
                    disabled={sending || !noteBody.trim()}
                    className="flex-1 flex items-center justify-center gap-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-[11px] font-bold py-1.5 rounded-lg">
                    {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3" /> Send</>}
                  </button>
                  <button onClick={() => { setQuickNoteFor(null); setNoteBody(""); }}
                    className="px-3 text-[11px] font-bold text-slate-500 hover:text-slate-700">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-1.5">
                <button data-testid={`digest-note-${t.pdi_user_id}`} onClick={() => setQuickNoteFor(t.pdi_user_id)}
                  className="flex items-center justify-center gap-1 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-[11px] font-bold py-1.5 rounded-lg">
                  <MessageSquare className="w-3 h-3" /> Coaching note
                </button>
                <button data-testid={`digest-open-${t.pdi_user_id}`} onClick={() => onOpen(t.pdi_user_id)}
                  className="flex items-center justify-center gap-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[11px] font-bold py-1.5 rounded-lg">
                  <TrendingUp className="w-3 h-3" /> Full progress
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Accept Invite page ──
export function SupervisorAccept() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const token = sp.get("token") || "";
  const [status, setStatus] = useState("idle"); // idle | accepting | done | error
  const [pdiName, setPdiName] = useState("");

  const accept = async () => {
    setStatus("accepting");
    try {
      const { data } = await axios.post(`${API}/api/supervisor/accept`, { token }, { withCredentials: true });
      setPdiName(data.pdi_name || "your trainee");
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  if (!user) {
    return (
      <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120] p-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-sm w-full text-center space-y-3">
          <Shield className="w-10 h-10 text-indigo-500 mx-auto" />
          <p className="text-base font-extrabold text-slate-900 dark:text-white">Supervisor Invite</p>
          <p className="text-sm text-slate-500">Sign in or create an account to accept this supervision role.</p>
          <Button onClick={() => nav(`/login?redirect=${encodeURIComponent(`/supervisor/accept?token=${token}`)}`)}
            className="w-full h-11 bg-indigo-600 text-white rounded-xl font-bold">Sign In / Sign Up</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-50 dark:bg-[#0c1120] p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-sm w-full text-center space-y-3">
        <Shield className="w-10 h-10 text-indigo-500 mx-auto" />
        {status === "idle" && (
          <>
            <p className="text-base font-extrabold text-slate-900 dark:text-white">Accept Supervisor Role?</p>
            <p className="text-sm text-slate-500">You'll get a light portal showing the trainee's Part 3 progress. You don't need to use this platform for your own business.</p>
            <Button data-testid="accept-invite-btn" onClick={accept} className="w-full h-11 bg-indigo-600 text-white rounded-xl font-bold">Accept</Button>
          </>
        )}
        {status === "accepting" && <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto" />}
        {status === "done" && (
          <>
            <Check className="w-10 h-10 text-emerald-500 mx-auto" />
            <p className="text-base font-extrabold text-slate-900 dark:text-white">You're now supervising {pdiName}</p>
            <Button onClick={() => nav("/supervisor")} className="w-full h-11 bg-indigo-600 text-white rounded-xl font-bold">Open Portal</Button>
          </>
        )}
        {status === "error" && (
          <>
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
            <p className="text-base font-extrabold text-slate-900 dark:text-white">Invite is invalid or expired</p>
            <Button onClick={() => nav("/")} variant="outline" className="w-full h-11">Close</Button>
          </>
        )}
      </div>
    </div>
  );
}
